import { useEffect, useState, useRef } from 'react';
import { api } from '../lib/api';

interface Build {
  id: string; version: string; platform: string; downloadUrl: string;
  sha256: string; sizeBytes: number; releaseNotes: string | null;
  isActive: boolean; uploadedAt: string;
}

function formatBytes(bytes: number): string {
  return `${(bytes / 1_048_576).toFixed(1)} MB`;
}

export default function Builds() {
  const [builds, setBuilds] = useState<Build[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [version, setVersion] = useState('');
  const [platform, setPlatform] = useState('android');
  const [releaseNotes, setReleaseNotes] = useState('');
  const [activate, setActivate] = useState(true);
  const fileRef = useRef<HTMLInputElement>(null);

  async function load() {
    setLoading(true);
    try {
      const data = await api.getBuilds();
      setBuilds(data.builds as Build[]);
    } catch (e) { setMessage(String(e)); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  async function handleUpload(e: React.FormEvent) {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file || !version) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('version', version);
      fd.append('platform', platform);
      fd.append('releaseNotes', releaseNotes);
      fd.append('activate', String(activate));
      await api.uploadBuild(fd);
      setMessage('Build uploaded successfully');
      setVersion(''); setReleaseNotes('');
      if (fileRef.current) fileRef.current.value = '';
      load();
    } catch (e) { setMessage(String(e)); }
    finally { setUploading(false); }
  }

  async function handleActivate(id: string) {
    try {
      const res = await api.activateBuild(id);
      setMessage(res.message);
      load();
    } catch (e) { setMessage(String(e)); }
  }

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Build Distribution</h1>
      {message && <div className="mb-4 p-3 bg-blue-50 text-blue-800 rounded">{message}</div>}

      {/* Upload form */}
      <div className="bg-white rounded-lg border p-5 mb-6">
        <h2 className="text-base font-semibold mb-4">Upload new build</h2>
        <form onSubmit={handleUpload} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm mb-1">Version *</label>
            <input className="border rounded px-3 py-2 w-full" placeholder="1.2.0" value={version} onChange={(e) => setVersion(e.target.value)} required />
          </div>
          <div>
            <label className="block text-sm mb-1">Platform *</label>
            <select className="border rounded px-3 py-2 w-full" value={platform} onChange={(e) => setPlatform(e.target.value)}>
              <option value="android">Android APK</option>
              <option value="ios">iOS IPA</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm mb-1">APK/IPA file *</label>
            <input ref={fileRef} type="file" accept=".apk,.ipa" className="border rounded px-3 py-2 w-full" required />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm mb-1">Release notes</label>
            <textarea className="border rounded px-3 py-2 w-full" rows={3} value={releaseNotes} onChange={(e) => setReleaseNotes(e.target.value)} placeholder="What's new in this version?" />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="activate" checked={activate} onChange={(e) => setActivate(e.target.checked)} />
            <label htmlFor="activate" className="text-sm">Set as active build immediately</label>
          </div>
          <div className="flex justify-end">
            <button type="submit" disabled={uploading} className="px-6 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50">
              {uploading ? 'Uploading...' : 'Upload'}
            </button>
          </div>
        </form>
      </div>

      {/* Build list */}
      {loading ? <p className="text-gray-500">Loading...</p> : (
        <div className="overflow-x-auto rounded border">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 text-left">
              <tr>
                <th className="px-4 py-2">Version</th>
                <th className="px-4 py-2">Platform</th>
                <th className="px-4 py-2">Size</th>
                <th className="px-4 py-2">SHA-256</th>
                <th className="px-4 py-2">Uploaded</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {builds.map((b) => (
                <tr key={b.id} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-2 font-semibold">{b.version}</td>
                  <td className="px-4 py-2">{b.platform}</td>
                  <td className="px-4 py-2">{formatBytes(b.sizeBytes)}</td>
                  <td className="px-4 py-2 font-mono text-xs text-gray-500">{b.sha256.slice(0, 12)}...</td>
                  <td className="px-4 py-2 text-gray-500">{new Date(b.uploadedAt).toLocaleDateString()}</td>
                  <td className="px-4 py-2">
                    {b.isActive && <span className="px-2 py-0.5 rounded text-xs bg-green-100 text-green-700 font-medium">Active</span>}
                  </td>
                  <td className="px-4 py-2 flex gap-2">
                    <a href={b.downloadUrl} className="text-xs px-2 py-1 bg-gray-100 rounded hover:bg-gray-200">Download</a>
                    {!b.isActive && (
                      <button onClick={() => handleActivate(b.id)} className="text-xs px-2 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700">
                        Activate
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
